import { useQuery } from "@apollo/client";
import a from "indefinite";
import { compact } from "lodash";
import moment from "moment";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  AssessmentGroupDelivery,
  AssessmentGroupProviders,
  AssessmentType,
  ComplianceProgramMissingAssessmentFragment,
  ComplianceProgramParticipantStatus,
  ComplianceProgramPopoverContentAssessmentDeliveryFragment,
  ComplianceProgramPopoverContentAssessmentFragment,
  ComplianceProgramPopoverContentMeetingFragment,
  GetComplianceProgramPopoverContentQuery,
  GetComplianceProgramPopoverContentQueryVariables,
  GetComplianceProgramProgressQuery,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import AppLink from "@components/link/link";
import Loading from "@components/loading/loading";
import useDebounce from "@components/use-debounce/use-debounce";
import {
  assertEdgesNonNull,
  assertEdgesNonNullWithStringId,
  getUrl,
  getUrlUsingTypename,
} from "@helpers/helpers";
import { pluralize } from "@helpers/string";

import getComplianceProgramPopoverContentQuery from "../graphql/get-compliance-program-popover-content-query";
import {
  ComplianceProgramReportingEntityTableCol,
  ComplianceProgramReportingEntityTableParticipantStatusWithNominations,
} from "./compliance-program-progress-entity-table";

const ProgressPillPopoverItem = ({
  assessment,
  oneonone,
  assessmentDelivery,
}: {
  oneonone?: ComplianceProgramPopoverContentMeetingFragment;
  assessment?:
    | ComplianceProgramPopoverContentAssessmentFragment
    | ComplianceProgramMissingAssessmentFragment;
  assessmentDelivery?: ComplianceProgramPopoverContentAssessmentDeliveryFragment;
}) => {
  const label = useLabel();
  const location = useLocation();

  if (!assessment && !oneonone && !assessmentDelivery) {
    throw new Error("No assessment, delivery, or oneonone provided");
  }
  if (oneonone) {
    const content = `${oneonone.title}`;
    const suffix = oneonone.isFinalized
      ? `finalized on ${moment(oneonone.finalizedAt).format("lll")}`
      : oneonone.startDatetime
        ? `scheduled for ${moment(oneonone.startDatetime).format("lll")}`
        : `drafted`;
    return (
      <li key={oneonone.id}>
        {oneonone.canRead?.permission ? (
          <>
            <AppLink
              to={getUrl({
                meetingGroupId: oneonone.meetingGroupId,
                meetingId: oneonone.id,
              })}
              className="hover:underline text-blue-500"
            >
              {content}
            </AppLink>{" "}
            {suffix}
          </>
        ) : (
          <span>
            {content} {suffix}
          </span>
        )}
      </li>
    );
  }
  if (assessment) {
    const content =
      assessment.__typename === "AssessmentNode" ? (
        <span>
          {assessment.responder?.name}
          {assessment.submittedDatetime
            ? ` on ${moment(assessment.submittedDatetime).format("lll")}`
            : ""}
        </span>
      ) : (
        <span>{assessment.responder?.name}</span>
      );
    return assessment.__typename === "AssessmentNode" ? (
      <li key={assessment.id}>
        {assessment.canRead?.permission ? (
          <AppLink
            to={`/assessments/assessment/${assessment.id}`}
            className="hover:underline text-blue-500"
          >
            {content}
          </AppLink>
        ) : (
          <span>{content}</span>
        )}
      </li>
    ) : (
      <li key={assessment.responder?.id}>
        <span>{content}</span>
      </li>
    );
  }
  if (assessmentDelivery) {
    return (
      <li>
        <AppLink
          to={{
            pathname: getUrlUsingTypename(assessmentDelivery) as string,
            state: {
              previousPathname: `${location.pathname}${location.search}`,
            },
          }}
          className="hover:underline text-blue-500"
        >
          {`${label("review", { capitalize: true })} package for ${
            assessmentDelivery.target?.name
          }`}
        </AppLink>
      </li>
    );
  }
};

const ProgramProgressPillPopoverPanel = ({
  col,
  participantStatus,
  status,
  complianceProgram,
}: {
  participantStatus: ComplianceProgramReportingEntityTableParticipantStatusWithNominations;
  col: ComplianceProgramReportingEntityTableCol;
  status: ComplianceProgramParticipantStatus;
  complianceProgram: NonNullable<
    GetComplianceProgramProgressQuery["complianceProgram"]
  >;
}) => {
  const label = useLabel();
  const managers = participantStatus.user.managersList;
  const hasNoManagers = managers.length === 0;
  const participantName = participantStatus.user.name;
  const isNotApplicable =
    status === ComplianceProgramParticipantStatus.NotApplicable;
  const isCompleted = status === ComplianceProgramParticipantStatus.Complete;
  const isInProgress = status === ComplianceProgramParticipantStatus.InProgress;
  const isNotStarted = status === ComplianceProgramParticipantStatus.NotStarted;
  const isPerformanceAssessment =
    col.keyName === "performanceAssessmentStatus" &&
    complianceProgram.performanceAssessmentTemplate;
  const isPerformanceSelfAssessment =
    col.keyName === "performanceSelfAssessmentStatus" &&
    complianceProgram.performanceAssessmentTemplate;
  const isPeerAssessment =
    col.keyName === "peerAssessmentStatus" &&
    complianceProgram.peerAssessmentTemplate;
  const isEngagementSurvey =
    col.keyName === "engagementSurveyStatus" &&
    complianceProgram.engagementSurveyTemplate;
  const isManagerAssessment =
    col.keyName === "managerAssessmentStatus" &&
    complianceProgram.managerAssessmentTemplate;
  const isAdminApproval = useMemo(
    () =>
      col.keyName === "deliveryStatus" &&
      compact([
        complianceProgram.performanceAssessmentTemplate,
        complianceProgram.managerAssessmentTemplate,
        complianceProgram.peerAssessmentTemplate,
      ]).some(
        (template) =>
          template.delivery === AssessmentGroupDelivery.AdminApproval,
      ),
    [col.keyName, complianceProgram],
  );

  const usersMissingAssessment = useMemo(
    () =>
      complianceProgram?.usersMissingAssessment
        ? compact(
            complianceProgram.usersMissingAssessment.edges.map(
              (edge) => edge?.node,
            ),
          )
        : [],
    [complianceProgram],
  );

  const assessmentsOpenForNominations = useMemo(() => {
    return complianceProgram?.assessmentsOpenForNominations
      ? assertEdgesNonNullWithStringId(
          complianceProgram.assessmentsOpenForNominations,
        )
      : [];
  }, [complianceProgram]);

  const nominationCountByUserId = useMemo(() => {
    return assessmentsOpenForNominations.reduce(
      (memo, assessmentOpenForNominations) => {
        const userId = assessmentOpenForNominations.targetUser.id;
        memo[userId] = assessmentOpenForNominations.nominations.totalCount;
        return memo;
      },
      {} as Record<number, number>,
    );
  }, [assessmentsOpenForNominations]);

  // Nominations
  const statusText = useMemo(() => {
    let statusText = "";
    if (col.keyName === "nominations") {
      const nominations =
        nominationCountByUserId[participantStatus.user.id] > 0
          ? ComplianceProgramParticipantStatus.Complete
          : ComplianceProgramParticipantStatus.NotStarted;
      statusText =
        complianceProgram.peerAssessmentTemplate &&
        nominations === ComplianceProgramParticipantStatus.NotStarted
          ? `${
              complianceProgram.peerAssessmentTemplate.providers ===
              AssessmentGroupProviders.SubjectSelect
                ? `${participantName} needs`
                : `${participantName}'s manager needs`
            } to nominate peers for the peer assessment.`
          : "";
    }

    // Peer assessment tooltips
    if (isPeerAssessment && isNotApplicable) {
      if (
        complianceProgram.peerAssessmentTemplate.providers ===
        AssessmentGroupProviders.Default
      ) {
        const prefix = `In this peer ${label(
          "assessment",
        )}, peers who report to ${participantName}'s manager are automatically chosen.`;
        statusText = hasNoManagers
          ? `${prefix} However ${participantName} does not have a manager.`
          : `${prefix} However the manager of ${participantName} has no other reports available to assess ${participantName}.`;
      } else {
        statusText = "Subject has no nominated peers";
      }
    }

    // Performance assessment tooltips
    if (isPerformanceAssessment && isNotApplicable) {
      statusText = complianceProgram.performanceAssessmentTemplate
        .isOnlySelfAssessment
        ? `Self ${label("review")} only`
        : hasNoManagers
          ? "Subject has no manager"
          : "";
    }

    if (isPerformanceSelfAssessment && isNotApplicable && hasNoManagers) {
      statusText = "Subject has no managers";
    }

    if (col.keyName === "oneononeStatus" && isNotStarted) {
      if (hasNoManagers) {
        statusText = `${participantName} has no managers`;
      } else {
        statusText = `${managers
          .map((manager) => manager.name)
          .join(", ")} ${pluralize("need", managers.length)} to organize ${a(
          label("1-on-1"),
        )} with ${participantName}`;
      }
    }

    if (isManagerAssessment && isNotApplicable) {
      statusText = complianceProgram.managerAssessmentTemplate
        .isOnlySelfAssessment
        ? `Self ${label("review")} only`
        : "Subject has no reports";
    }

    if (isAdminApproval) {
      if (isInProgress) {
        statusText = "Admin has approved the delivery package";
      } else if (isNotStarted) {
        statusText = `Awaiting ${label("review")} submission`;
      } else if (isNotApplicable) {
        statusText = "Admin approval not required";
      }
    }

    if (isEngagementSurvey && isInProgress) {
      statusText = "Subject needs to finish completing the survey";
    }

    if (!statusText && isCompleted) {
      statusText = "Completed";
    }
    return statusText;
  }, [
    col.keyName,
    isPeerAssessment,
    isNotApplicable,
    isPerformanceAssessment,
    isPerformanceSelfAssessment,
    hasNoManagers,
    isNotStarted,
    isManagerAssessment,
    isAdminApproval,
    isEngagementSurvey,
    isInProgress,
    isCompleted,
    nominationCountByUserId,
    participantStatus.user.id,
    complianceProgram,
    participantName,
    label,
    managers,
  ]);

  const templateId = useMemo(() => {
    return complianceProgram.requiredTopicTemplates?.edges[0]?.node?.id;
  }, [complianceProgram]);
  const startDatetimeGte = useMemo(() => {
    return moment(complianceProgram.startDate).startOf("day").format();
  }, [complianceProgram.startDate]);
  const { data, loading: loadingAssessments } = useQuery<
    GetComplianceProgramPopoverContentQuery,
    GetComplianceProgramPopoverContentQueryVariables
  >(getComplianceProgramPopoverContentQuery, {
    variables: {
      complianceProgramId: complianceProgram.id,
      fetchAssessments: col.keyName.includes("Assessment"),
      fetchOneonones: col.keyName === "oneononeStatus",
      fetchAssessmentDeliveries: col.keyName === "deliveryStatus",
      assessmentTargetId:
        !isNotApplicable && (isPerformanceAssessment || isAdminApproval)
          ? participantStatus.user.id
          : undefined,
      assessmentResponderId:
        !isNotApplicable && isPerformanceSelfAssessment
          ? participantStatus.user.id
          : undefined,
      oneononeStartDatetimeGte: startDatetimeGte,
      oneononeTemplateId: templateId,
      oneononeParticipants: [participantStatus.user.id],
    },
    skip: status === ComplianceProgramParticipantStatus.NotApplicable,
  });
  const loading = useDebounce(loadingAssessments, 1000);
  const oneonones = useMemo(() => {
    return data?.complianceProgram?.matchingOneonones
      ? assertEdgesNonNull(data.complianceProgram.matchingOneonones)
      : [];
  }, [data]);
  const assessmentDeliveries = useMemo(() => {
    return data?.complianceProgram?.assessmentDeliveries
      ? assertEdgesNonNull(data.complianceProgram.assessmentDeliveries)
      : [];
  }, [data]);
  const allAssessments = useMemo(
    () =>
      data?.complianceProgram?.assessments
        ? assertEdgesNonNull(data.complianceProgram.assessments)
        : [],
    [data],
  );
  const assessments = useMemo(
    () =>
      allAssessments.filter((assessment) => {
        if (isPerformanceAssessment) {
          return (
            assessment.template.assessmentType === AssessmentType.Performance &&
            assessment.target?.id === participantStatus.user.id &&
            assessment.responder?.id !== participantStatus.user.id
          );
        }
        if (isPerformanceSelfAssessment) {
          return (
            assessment.template.assessmentType === AssessmentType.Performance &&
            assessment.target?.id === participantStatus.user.id &&
            assessment.responder?.id === participantStatus.user.id
          );
        }
        if (isPeerAssessment) {
          return (
            assessment.template.assessmentType === AssessmentType.Peer &&
            assessment.target?.id === participantStatus.user.id &&
            assessment.responder?.id !== participantStatus.user.id
          );
        }
        if (isManagerAssessment) {
          return (
            assessment.template.assessmentType === AssessmentType.Manager &&
            assessment.target?.id === participantStatus.user.id &&
            assessment.responder?.id !== participantStatus.user.id
          );
        }
        return false;
      }),
    [
      allAssessments,
      isPerformanceAssessment,
      isPerformanceSelfAssessment,
      isPeerAssessment,
      isManagerAssessment,
      participantStatus.user.id,
    ],
  );
  const assessmentsInProgress = useMemo(
    () => assessments.filter((assessment) => !assessment.submittedDatetime),
    [assessments],
  );
  const completedAssessments = useMemo(
    () => assessments.filter((assessment) => assessment.submittedDatetime),
    [assessments],
  );
  const assessmentUIds = useMemo(
    () =>
      assessments.map(
        (assessment) =>
          `${assessment.template.id}-${assessment.responder?.id}-${assessment.target?.id}`,
      ),
    [assessments],
  );
  const missingAssessments = useMemo(
    () =>
      usersMissingAssessment.filter((missingAssessment) => {
        const uid = `${missingAssessment.assessmentTemplate.id}-${missingAssessment.responder?.id}-${missingAssessment.target?.id}`;
        if (assessmentUIds.includes(uid)) {
          return false;
        }
        if (isPerformanceAssessment) {
          return (
            missingAssessment.assessmentTemplate.assessmentType ===
              AssessmentType.Performance &&
            missingAssessment.target?.id === participantStatus.user.id &&
            missingAssessment.responder?.id !== participantStatus.user.id
          );
        }
        if (isPerformanceSelfAssessment) {
          return (
            missingAssessment.assessmentTemplate.assessmentType ===
              AssessmentType.Performance &&
            missingAssessment.target?.id === participantStatus.user.id &&
            missingAssessment.responder?.id === participantStatus.user.id
          );
        }
        if (isPeerAssessment) {
          return (
            missingAssessment.assessmentTemplate.assessmentType ===
              AssessmentType.Peer &&
            missingAssessment.target?.id === participantStatus.user.id &&
            missingAssessment.responder?.id !== participantStatus.user.id
          );
        }
        if (isManagerAssessment) {
          return (
            missingAssessment.assessmentTemplate.assessmentType ===
              AssessmentType.Manager &&
            missingAssessment.target?.id === participantStatus.user.id &&
            missingAssessment.responder?.id !== participantStatus.user.id
          );
        }
        return false;
      }),
    [
      usersMissingAssessment,
      assessmentUIds,
      isPerformanceAssessment,
      isPerformanceSelfAssessment,
      isPeerAssessment,
      isManagerAssessment,
      participantStatus.user.id,
    ],
  );

  return (
    <div className="flex flex-col gap-4 z-1 bg-white rounded-md px-4 pt-2 pb-3">
      {statusText && (
        <div>
          <div className="text-2xs text-gray-400 font-medium mb-0.5">
            Status
          </div>
          <div className="text-sm text-gray-700">{statusText}</div>
        </div>
      )}
      {!isNotApplicable && (
        <>
          {loading ? (
            <div>
              <Loading size="5" />
            </div>
          ) : (
            <>
              {assessmentsInProgress.length > 0 && (
                <div>
                  <div className="text-2xs text-gray-400 font-medium mb-0.5">
                    {label("review", { capitalize: true, pluralize: true })} in
                    Progress by
                  </div>
                  {assessmentsInProgress.length > 0 && (
                    <ul className="text-sm text-gray-700 list-disc list-inside ml-1">
                      {assessmentsInProgress.map((assessment) => (
                        <ProgressPillPopoverItem
                          assessment={assessment}
                          key={assessment.id}
                        />
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {missingAssessments.length > 0 && (
                <div>
                  <div className="text-2xs text-gray-400 font-medium mb-0.5">
                    Missing {label("review", { pluralize: true })} by
                  </div>
                  {missingAssessments.length > 0 && (
                    <ul className="text-sm text-gray-700 list-disc list-inside ml-1">
                      {missingAssessments.map((assessment) => (
                        <ProgressPillPopoverItem
                          assessment={assessment}
                          key={assessment.responder?.id}
                        />
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {completedAssessments.length > 0 && (
                <div>
                  <div className="text-2xs text-gray-400 font-medium mb-0.5">
                    Completed {label("review", { pluralize: true })} by
                  </div>
                  {completedAssessments.length > 0 && (
                    <ul className="text-sm text-gray-700 list-disc list-inside ml-1">
                      {completedAssessments.map((assessment) => (
                        <ProgressPillPopoverItem
                          assessment={assessment}
                          key={assessment.id}
                        />
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {oneonones.length > 0 && (
                <div>
                  <div className="text-2xs text-gray-400 font-medium mb-0.5">
                    {label("1-on-1", { capitalize: true, pluralize: true })}
                  </div>
                  {oneonones.length > 0 && (
                    <ul className="text-sm text-gray-700 list-disc list-inside ml-1">
                      {oneonones.map((oneonone) => (
                        <ProgressPillPopoverItem
                          oneonone={oneonone}
                          key={oneonone.id}
                        />
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {assessmentDeliveries.length > 0 && (
                <div>
                  <div className="text-2xs text-gray-400 font-medium mb-0.5">
                    {label("review", { pluralize: true, capitalize: true })}{" "}
                    awaiting approval
                  </div>
                  {assessmentDeliveries.length > 0 && (
                    <ul className="text-sm text-gray-700 list-disc list-inside ml-1">
                      {assessmentDeliveries.map((assessmentDelivery) => (
                        <ProgressPillPopoverItem
                          assessmentDelivery={assessmentDelivery}
                          key={assessmentDelivery.id}
                        />
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {col.keyName.includes("Assessment") &&
                missingAssessments.length === 0 &&
                completedAssessments.length === 0 &&
                assessmentsInProgress.length === 0 && (
                  <div>
                    <div className="text-2xs text-gray-400 font-medium mb-0.5">
                      {label("review", {
                        pluralize: true,
                        capitalize: true,
                      })}
                    </div>
                    <div className="text-sm text-gray-700">
                      No {label("review", { pluralize: true })}.
                    </div>
                  </div>
                )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ProgramProgressPillPopoverPanel;
